import { useCallback, useEffect, useRef } from "react";
import debounce from "lodash/debounce";
import { SEARCH_SCROLL_DEBOUNCE_TIME } from "../../utils/constants";

export function useListVisibilityWatcher<T extends HTMLElement>(
  onNewElementsInViewport: (oldLastSeen: number, newLastSeen: number) => void,
  elementQuerySelector: string,
  dependencies: unknown[],
) {
  const listRef = useRef<T>(null);
  const lastSeenRef = useRef(0);

  const updateLastSeen = useCallback(
    () => {
      if (listRef.current) {
        const listElement = listRef.current;
        const bb = listElement.getBoundingClientRect();
        const isVisible = bb.height > 0 || bb.width > 0;
        if (isVisible) {
          const oldLastSeen = lastSeenRef.current;
          let lastSeen = oldLastSeen;

          const elements = Array.prototype.slice.call(listElement.querySelectorAll(elementQuerySelector), lastSeen);
          for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            const dimensions = element.getBoundingClientRect();
            if (-window.innerHeight + dimensions.top + dimensions.height / 4 <= 0) {
              lastSeen++;
            } else {
              break;
            }
          }
          lastSeenRef.current = lastSeen;
          onNewElementsInViewport(oldLastSeen, lastSeen);
        }
        if (!isVisible) {
          window.setTimeout(updateLastSeen, 1000);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ([onNewElementsInViewport, elementQuerySelector] as unknown[]).concat(dependencies),
  );

  useEffect(() => {
    const debouncedFn = debounce(updateLastSeen, SEARCH_SCROLL_DEBOUNCE_TIME);
    window.addEventListener("scroll", debouncedFn);
    return () => window.removeEventListener("scroll", debouncedFn);
  }, [updateLastSeen]);

  useEffect(
    () => {
      lastSeenRef.current = 0;
      updateLastSeen();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    dependencies,
  );

  return listRef;
}
